import { Box, Stack, Tooltip, Typography } from "@mui/material";

import { type AudienceReachMedium } from "@/api/audiences";
import { InfoOutlinedIcon } from "@/icons";
import OrionSwitch from "@/orion/OrionComponents/OrionSwitch";

export interface EnforceFrequencyCapSectionProps {
  medium: AudienceReachMedium;
  enforceFrequencyCap: boolean;
  onEnforceFrequencyCapChange: (value: boolean) => void;
  disabled?: boolean;
}

const TOOLTIPS: Record<AudienceReachMedium, { on: string; off: string }> = {
  MEDIUM_TYPE_SMS: {
    on: "Sends to every recipient, even those who've already hit their text limit for today or this week. Turn off to respect each recipient's limit (2 texts/day, 10/week).",
    off: "Respects each recipient's text limit (2 texts/day, 10/week) — anyone who's hit it is skipped. Turn on to send to everyone regardless.",
  },
  MEDIUM_TYPE_EMAIL: {
    on: "Sends to every recipient, even those who've already hit their email limit. Turn off to respect each recipient's limit (set in Settings → Email).",
    off: "Respects each recipient's email limit (set in Settings → Email) — anyone who's hit it is skipped. Turn on to send to everyone regardless.",
  },
};

export function EnforceFrequencyCapSection({
  medium,
  enforceFrequencyCap,
  onEnforceFrequencyCapChange,
  disabled,
}: EnforceFrequencyCapSectionProps) {
  const overrideEnabled = !enforceFrequencyCap;
  const tooltips = TOOLTIPS[medium];
  return (
    <Stack
      gap={3}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.primary.main}`,
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        px: 4,
        py: 3,
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={3}
        data-testid="enforce-frequency-cap-toggle"
      >
        <OrionSwitch
          checked={overrideEnabled}
          disabled={disabled}
          onChange={(_event, next) => onEnforceFrequencyCapChange(!next)}
          slotProps={{
            input: { "aria-label": "Override Frequency Caps", role: "switch" },
          }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Override Frequency Caps
        </Typography>
        <Tooltip title={overrideEnabled ? tooltips.on : tooltips.off} arrow>
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }}
            />
          </Box>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
